/** @format */

import React, { useEffect, useState } from "react";
import Table from "../../extra/Table";
import Pagination from "../../extra/Pagination";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { colors } from "../../../util/SkeletonColor";
import dayjs from "dayjs";
import DateRangePicker1 from "react-bootstrap-daterangepicker";
import { getRefundableOrders, initiateRefund, rejectRefund } from "../../store/refund/refund.action";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../extra/Button";
import defaultImage from "../../../assets/images/default.jpg";
import { Box, Modal, Typography, CircularProgress } from "@mui/material";
import { setToast } from "../../../util/toast";

const AdminRefund = () => {
	const dispatch = useDispatch();
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [currentPage, setCurrentPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [sDate, setsDate] = useState("All");
	const [eDate, seteDate] = useState("All");

	// Refund confirmation modal state
	const [openRefund, setOpenRefund] = useState(false);
	const [refundOrder, setRefundOrder] = useState(null);
	const [refundLoading, setRefundLoading] = useState(false);

	// Reject modal state
	const [openReject, setOpenReject] = useState(false);
	const [rejectOrderId, setRejectOrderId] = useState(null);
	const [rejectReason, setRejectReason] = useState("");

	const modalStyle = {
		position: "absolute",
		top: "50%",
		left: "50%",
		transform: "translate(-50%, -50%)",
		width: 600,
		backgroundColor: "background.paper",
		borderRadius: "13px",
		border: "1px solid #C9C9C9",
		boxShadow: "24px",
		padding: "19px",
	};

	const { refundableOrders, total } = useSelector((state) => state.refund);

	useEffect(() => {
		dispatch(getRefundableOrders(currentPage, rowsPerPage, sDate, eDate));
	}, [currentPage, rowsPerPage, sDate, eDate]);

	useEffect(() => {
		setData(refundableOrders);
	}, [refundableOrders]);

	useEffect(() => {
		const timer = setTimeout(() => {
			setLoading(false);
		}, 1500);
		return () => clearTimeout(timer);
	}, []);

	const handleChangePage = (event, newPage) => {
		setCurrentPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event), 10);
		setCurrentPage(1);
	};

	const handleApply = (event, picker) => {
		picker.element.val(picker.startDate.format("YYYY-MM-DD") + " / " + picker.endDate.format("YYYY-MM-DD"));
		setsDate(dayjs(picker.startDate).format("YYYY-MM-DD"));
		seteDate(dayjs(picker.endDate).format("YYYY-MM-DD"));
	};

	const handleCancel = (event, picker) => {
		picker.element.val("");
		setsDate("All");
		seteDate("All");
	};

	// ─── Refund Modal ───
	const handleOpenRefund = (order) => {
		setRefundOrder(order);
		setOpenRefund(true);
	};

	const handleCloseRefund = () => {
		setOpenRefund(false);
		setRefundOrder(null);
		setRefundLoading(false);
	};

	const handleSubmitRefund = async () => {
		setRefundLoading(true);
		const result = await dispatch(
			initiateRefund({
				orderId: refundOrder._id,
			}),
		);
		if (result?.success) {
			handleCloseRefund();
		}
		setRefundLoading(false);
	};

	// ─── Reject Modal ───
	const handleOpenReject = (orderId) => {
		setRejectOrderId(orderId);
		setRejectReason("");
		setOpenReject(true);
	};

	const handleCloseReject = () => {
		setOpenReject(false);
		setRejectOrderId(null);
		setRejectReason("");
	};

	const handleSubmitReject = () => {
		dispatch(rejectRefund({ orderId: rejectOrderId, reason: rejectReason }));
		handleCloseReject();
	};

	// Refund status badge
	const getRefundBadge = (order) => {
		const rr = order?.refundRequest;
		if (!rr) {
			return <span style={{ backgroundColor: "#FEF3C7", color: "#D97706", padding: "4px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: 600 }}>Pending</span>;
		}
		if (rr.status === 4 || rr.paystackRefundStatus === "pending" || rr.paystackRefundStatus === "processing") {
			return <span style={{ backgroundColor: "#DBEAFE", color: "#2563EB", padding: "4px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: 600 }}>⏳ Processing</span>;
		}
		if (rr.status === 2 || rr.paystackRefundStatus === "processed") {
			return <span style={{ backgroundColor: "#D1FAE5", color: "#059669", padding: "4px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: 600 }}>✅ Completed</span>;
		}
		if (rr.status === 5 || rr.paystackRefundStatus === "failed") {
			return <span style={{ backgroundColor: "#FEE2E2", color: "#DC2626", padding: "4px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: 600 }}>❌ Failed</span>;
		}
		if (rr.status === 3) {
			return <span style={{ backgroundColor: "#FEE2E2", color: "#DC2626", padding: "4px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: 600 }}>Rejected</span>;
		}
		return <span style={{ backgroundColor: "#FEF3C7", color: "#D97706", padding: "4px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: 600 }}>Pending</span>;
	};

	const mapData = [
		{
			Header: "No",
			width: "20px",
			Cell: ({ index }) => <span>{(currentPage - 1) * rowsPerPage + index + 1}</span>,
		},
		{
			Header: "Order ID",
			body: "orderId",
			Cell: ({ row }) => (
				<span className='mb-0' style={{ fontWeight: 600 }}>
					{row?.orderId}
				</span>
			),
		},
		{
			Header: "Buyer",
			body: "userId",
			Cell: ({ row }) => {
				const user = row?.userId;
				const imageUrl = user?.image || defaultImage;
				return (
					<div style={{ display: "flex", alignItems: "center", gap: "10px", justifyContent: "center" }}>
						{loading ? (
							<Skeleton height={35} width={35} style={{ borderRadius: "50%" }} baseColor={colors?.baseColor} highlightColor={colors?.highlightColor} />
						) : (
							<img
								src={imageUrl}
								height={35}
								width={35}
								alt='buyer'
								style={{ borderRadius: "50%", objectFit: "cover" }}
								onError={(e) => {
									e.target.onerror = null;
									e.target.src = defaultImage;
								}}
							/>
						)}
						<span className='mb-0'>
							{user?.firstName} {user?.lastName}
						</span>
					</div>
				);
			},
		},
		{
			Header: "Email",
			body: "email",
			Cell: ({ row }) => (
				<span className='mb-0' style={{ fontSize: "13px" }}>
					{row?.userId?.email || "N/A"}
				</span>
			),
		},
		{
			Header: "Amount (₦)",
			body: "finalTotal",
			Cell: ({ row }) => (
				<span className='mb-0' style={{ fontWeight: 600, color: "#059669" }}>
					₦{Number(row?.finalTotal || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
				</span>
			),
		},
		{
			Header: "Payment",
			body: "paymentGateway",
			Cell: ({ row }) => <span className='mb-0'>{row?.paymentGateway || "N/A"}</span>,
		},
		{
			Header: "Order Date",
			body: "createdAt",
			Cell: ({ row }) => <span>{dayjs(row.createdAt).format("DD MMM YYYY")}</span>,
		},
		{
			Header: "Refund Status",
			body: "refundStatus",
			Cell: ({ row }) => getRefundBadge(row),
		},
		{
			Header: "Action",
			body: "",
			Cell: ({ row }) => {
				const rr = row?.refundRequest;
				const isCompleted = rr && (rr.status === 2 || rr.paystackRefundStatus === "processed");
				const isProcessing = rr && (rr.status === 4 || rr.paystackRefundStatus === "pending" || rr.paystackRefundStatus === "processing");
				const isRejected = rr && rr.status === 3;

				if (isCompleted || isProcessing || isRejected) {
					return <span style={{ color: "#9CA3AF", fontSize: "12px" }}>—</span>;
				}

				return (
					<div className='d-flex justify-content-center align-items-center gap-2'>
						<button
							style={{
								backgroundColor: "#e54a13",
								color: "#fff",
								border: "none",
								borderRadius: "6px",
								padding: "6px 14px",
								fontSize: "12px",
								fontWeight: 600,
								cursor: "pointer",
							}}
							onClick={() => handleOpenRefund(row)}
							title='Initiate Refund'>
							Refund
						</button>
						<button
							style={{
								backgroundColor: "#fff",
								color: "#DC2626",
								border: "1px solid #DC2626",
								borderRadius: "6px",
								padding: "6px 12px",
								fontSize: "12px",
								fontWeight: 600,
								cursor: "pointer",
							}}
							onClick={() => handleOpenReject(row?._id)}
							title='Reject Refund'>
							Reject
						</button>
					</div>
				);
			},
		},
	];

	return (
		<div className='mainSellerTable'>
			<div className='sellerTable'>
				<div className='col-12 headname d-flex justify-content-between align-items-center'>
					<span>Refunds</span>
				</div>
				<div className='sellerMain'>
					<div className='tableMain mt-2'>
						<div className='sellerHeader primeHeader'>
							<div className='d-flex justify-content-end'>
								<div className='px-3'>
									<DateRangePicker1
										initialSettings={{
											applyButtonClasses: "btn-default",
											autoUpdateInput: false,
											locale: { cancelLabel: "Clear" },
											maxDate: new Date(),
										}}
										onApply={handleApply}
										onCancel={handleCancel}>
										<input
											type='text'
											className='form-control text-center datedash my-1 white-placeholder'
											placeholder='Select Date'
											readOnly
											style={{
												width: "13rem",
												fontWeight: 500,
												cursor: "pointer",
												fontSize: "14px",
												backgroundColor: "#fff",
											}}
										/>
									</DateRangePicker1>
								</div>
							</div>
						</div>
						<Table data={data} mapData={mapData} serverPerPage={rowsPerPage} serverPage={1} type={"server"} />
						<Pagination component='div' count={total} type={"server"} onPageChange={handleChangePage} serverPerPage={rowsPerPage} totalData={total} serverPage={currentPage} setCurrentPage={setCurrentPage} onRowsPerPageChange={handleChangeRowsPerPage} />
					</div>
				</div>
			</div>

			{/* ─── Refund Confirmation Modal ─── */}
			<Modal open={openRefund} onClose={handleCloseRefund}>
				<Box sx={modalStyle} className='decline-modal'>
					<div className='modal-header'>
						<Typography variant='h5' component='h2' sx={{ fontWeight: 600, color: "#1f2937" }}>
							Confirm Refund
						</Typography>
						{refundOrder && (
							<Typography variant='body2' sx={{ color: "#6B7280", mt: 1 }}>
								Order: <strong>{refundOrder.orderId}</strong> &nbsp;|&nbsp; Amount: <strong style={{ color: "#059669" }}>₦{Number(refundOrder.finalTotal || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}</strong>
								&nbsp;|&nbsp; Buyer:{" "}
								<strong>
									{refundOrder.userId?.firstName} {refundOrder.userId?.lastName}
								</strong>
							</Typography>
						)}
					</div>

					<div style={{ padding: "15px 15px 0 15px" }}>
						<div
							style={{
								backgroundColor: "#FFF7ED",
								border: "1px solid #FED7AA",
								borderRadius: "8px",
								padding: "14px 16px",
								marginBottom: "16px",
							}}>
							<Typography variant='body2' sx={{ color: "#92400E", fontWeight: 500 }}>
								This will refund <strong>₦{Number(refundOrder?.finalTotal || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}</strong> back to the customer's original payment method used during checkout.
							</Typography>
						</div>

						<div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", paddingTop: "10px", paddingBottom: "10px" }}>
							<button onClick={handleCloseRefund} className='close-btn'>
								Cancel
							</button>
							<button
								onClick={handleSubmitRefund}
								disabled={refundLoading}
								style={{
									backgroundColor: refundLoading ? "#ccc" : "#e54a13",
									color: "#fff",
									border: "none",
									borderRadius: "6px",
									padding: "8px 20px",
									fontWeight: 600,
									cursor: refundLoading ? "not-allowed" : "pointer",
									display: "flex",
									alignItems: "center",
									gap: "8px",
								}}>
								{refundLoading && <CircularProgress size={16} sx={{ color: "#fff" }} />}
								Confirm Refund
							</button>
						</div>
					</div>
				</Box>
			</Modal>

			{/* ─── Reject Refund Modal ─── */}
			<Modal open={openReject} onClose={handleCloseReject}>
				<Box sx={modalStyle} className='decline-modal'>
					<div className='modal-header'>
						<Typography variant='h5' component='h2' sx={{ fontWeight: 600, color: "#1f2937" }}>
							Reject Refund
						</Typography>
					</div>

					<div style={{ padding: "0px 15px" }}>
						<div className='mb-4'>
							<label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#374151", marginBottom: "8px" }}>Please provide a reason for rejecting this refund</label>
							<textarea style={{ width: "100%", padding: "10px 12px", border: "1px solid #D1D5DB", borderRadius: "8px", fontSize: "14px", outline: "none" }} rows={4} value={rejectReason} placeholder='Enter reason for rejecting this refund request...' onChange={(e) => setRejectReason(e.target.value)} />
						</div>

						<div className='modal-buttons d-flex justify-content-end' style={{ paddingBottom: "10px" }}>
							<button onClick={handleCloseReject} className='close-btn'>
								Cancel
							</button>
							<button onClick={handleSubmitReject} className='submit-btn' style={{ backgroundColor: "#e54a13" }}>
								Reject Refund
							</button>
						</div>
					</div>
				</Box>
			</Modal>
		</div>
	);
};

export default AdminRefund;
